import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE } from '../../config';
import { useTheme } from './ThemeContext';
import useVoiceAgent, { VoiceStatus } from './useVoiceAgent';

// ============================================================
// TYPES
// ============================================================
interface DirectorMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface PageContext {
  page_name: string;
  page_path: string;
  page_description?: string;
}

interface QuickAction {
  text: string;
  query: string;
}

interface SavedConversation {
  id: string;
  page_path: string;
  page_name: string;
  messages: DirectorMessage[];
  session_id: string | null;
  updated_at: number;
}

interface ExpertAgentProps {
  user: { id: number; email: string; is_admin: boolean };
  currentPath: string;
}

// ============================================================
// ICON COMPONENTS
// ============================================================
const IconDirector = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
  </svg>
);

const IconDirectorNavy = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0C2340" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
  </svg>
);

const IconX = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconSend = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const IconHistory = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconBack = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconMic = ({ color = 'currentColor', size = 18 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const IconMicOff = ({ color = 'currentColor', size = 18 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.13 1.49-.36 2.18" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

function voiceStatusLabel(status: VoiceStatus): string {
  switch (status) {
    case 'connecting': return 'Connecting…';
    case 'listening': return 'Listening…';
    case 'user_speaking': return 'Hearing you…';
    case 'processing': return 'Thinking…';
    case 'agent_speaking': return 'Director speaking…';
    case 'error': return 'Voice error';
    default: return '';
  }
}

// ============================================================
// CONVERSATION PERSISTENCE
// ============================================================
const STORAGE_KEY = 'director_conversations';
const MAX_CONVERSATIONS = 50;

function loadConversations(): SavedConversation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as SavedConversation[];
  } catch {
    return [];
  }
}

function saveConversations(conversations: SavedConversation[]): void {
  try {
    // Keep only the most recent conversations
    const trimmed = conversations.slice(0, MAX_CONVERSATIONS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage full or unavailable — silently fail
  }
}

function upsertConversation(
  convId: string,
  pagePath: string,
  pageName: string,
  messages: DirectorMessage[],
  sessionId: string | null,
): void {
  const conversations = loadConversations();
  const idx = conversations.findIndex(c => c.id === convId);
  const entry: SavedConversation = {
    id: convId,
    page_path: pagePath,
    page_name: pageName,
    messages,
    session_id: sessionId,
    updated_at: Date.now(),
  };
  if (idx >= 0) {
    conversations[idx] = entry;
  } else {
    conversations.unshift(entry);
  }
  // Sort by most recent
  conversations.sort((a, b) => b.updated_at - a.updated_at);
  saveConversations(conversations);
}

function deleteConversation(convId: string): void {
  const conversations = loadConversations().filter(c => c.id !== convId);
  saveConversations(conversations);
}

function generateConvId(): string {
  return `conv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// ============================================================
// PAGE CONTEXT MAPPING
// ============================================================
function getPageContext(pathname: string): PageContext {
  if (pathname === '/')
    return { page_name: 'Dashboard', page_path: pathname, page_description: 'Sentinel Dashboard showing Total Products, Made in America % (from COO distribution with HIGH/MODERATE/LOW confidence tiers), China-Dependent Products % (with confidence tiers), Risk Level (HIGH/MODERATE/LOW), Active Shortages (current FDA shortages count), Drug Country of Origin distribution (US/China/India/Other), API Country of Origin (Comtrade trade-flow estimates with mapped/not-mapped and China/India/USA/Other dependency breakdown), and CCP Affiliation breakdown (Drugs, API, Manufacturers). Note: there is NO MIA score displayed — the Made in America tile shows a percentage derived from country of origin distribution, not the made_in_america_score column.' };

  if (pathname === '/search')
    return { page_name: 'AI Search / Query', page_path: pathname, page_description: 'Natural language query interface for the drug product database. Shows Drug Intelligence Profile cards for product results with Risk and Confidence score rings, compliance status grid (BAA, TAA, COO, Recalls, Shortages), key supply chain facts, and search history.' };

  if (pathname === '/products')
    return { page_name: 'Products List', page_path: pathname, page_description: 'Server-side paginated product list showing: stat cards (Total Products, High Risk count, Made in America % from COO distribution, COO Confidence %), product table with columns (Product Name, Manufacturer, Risk level, COO Tier badge, Shortage status), filters (Risk, BAA eligibility, Country, Manufacturer, COO Tier, Shortage Status), and CSV export. Note: the Made in America stat is a percentage from COO distribution, NOT the made_in_america_score column.' };

  if (pathname.startsWith('/products/'))
    return { page_name: 'Product Detail', page_path: pathname, page_description: `Detail view for product ${pathname.split('/')[2]} with 6 tabs: Supply Chain Origin (COO score card, Made in USA score ring, confidence score, country risk flow diagram, COO history chart), Recalls (FDA recall cards with classification/status), Pricing Intelligence (NADAC pricing, therapeutic alternatives price comparison), Therapeutic Alternatives (sortable table of alternatives with risk/price/country), Manufacturer Intel (ownership, leadership, political donations), Data Sources (per-steward evidence breakdown with COO audit trail)` };

  if (pathname.startsWith('/clinical-drug/'))
    return { page_name: 'Clinical Drug Detail', page_path: pathname, page_description: 'RxNorm clinical drug detail showing: stat cards (Total Products, Total Packagings, Lowest NADAC, Highest NADAC), Pricing Summary (average NADAC with score ring), Compliance Summary (BAA eligible count, TAA eligible count), linked products list, and full packaging table with NADAC pricing per unit' };

  if (pathname === '/manufacturers')
    return { page_name: 'Manufacturers List', page_path: pathname, page_description: 'Server-side paginated manufacturer list showing: stat cards (Total Manufacturers, With Active Recalls, With Shortages, Products Covered, Not Linked), table with columns (Name, Country, Product Count, Total Recalls, Active Recalls, Shortages), filters (Country, Has Recalls, Has Shortages, COO Confidence), search, and CSV export' };

  if (pathname.startsWith('/manufacturers/'))
    return { page_name: 'Manufacturer Detail', page_path: pathname, page_description: 'Manufacturer profile with tabs: Overview (stats grid — Products, Total Recalls, Active Recalls, Shortages, Active Shortages; company details, corporate structure, subsidiaries, FDA registration summary), Products (with Made in USA score per product), Recalls (FDA recall cards), Shortages, FDA Sites (registered facilities with FDF/API manufacture flags), Import Refusals, Ownership (CCP screening matches, stock info, politician holdings), Leadership (executives with political donation data)' };

  if (pathname === '/country-origins')
    return { page_name: 'Country of Origin Analysis', page_path: pathname, page_description: 'Country-level breakdown showing: stat cards (Total Countries, Products Covered, Products Not Covered, Active Recalls, Active Shortages), searchable table with columns (Country Name, Product Count, High Confidence %, Active Recalls, Shortages, Status badge). Click a country to navigate to Products filtered by that country.' };

  if (pathname === '/risk-analyzer')
    return { page_name: 'Risk Analyzer', page_path: pathname, page_description: 'Portfolio risk analysis tool: Upload a CSV/Excel formulary file with NDC codes. After upload, shows risk dashboard with portfolio risk score ring, stat cards (Total Products, Matched, BAA Eligible, Avg MIA Score, High Risk, In Shortage), risk distribution chart, country concentration chart, top risk products table, filter tiles (All, Made in USA, India, China, CCP Impacted, Shortages), and MIA Gap Analysis with swap suggestions. Supports PDF and CSV export.' };

  if (pathname === '/china-dependency')
    return { page_name: 'China Dependency Analysis', page_path: pathname, page_description: 'CCP/China dependency dashboard showing: 4 navigation tiles (Drugs with any China dependency, APIs with CCP-flagged China dependency, Therapeutic Vulnerability classes exposed, CCP Connection manufacturers), expandable detail sections for each tile, dependency breakdown chart (API Sourcing, Finished Goods, Both, COO Only), therapeutic class exposure table, CCP-connected manufacturers list with screening match details, and server-paginated China-dependent products table' };

  if (pathname.startsWith('/geopolitical'))
    return { page_name: 'Geopolitical Intelligence', page_path: pathname, page_description: 'Country Risk Assessment view showing: stat cards (Total Countries, Drugs Linked, Not Linked, Active Recalls, Active Shortages), sortable country table with Risk Level (Critical/High/Medium/Low), product concentration, high confidence %, recalls, and shortages per country. Sort by Risk Concentration, Risk Level, or Country name.' };

  if (pathname.startsWith('/shortage-warning'))
    return { page_name: 'Shortage Early Warning', page_path: pathname, page_description: 'Shortage intelligence center with 4 tabs: Active Shortages (shortage cards with severity, country, cause, type — Limited/Unavailable, summary tiles for top countries and causes), Risk Radar (predictive risk scores at 30d/60d/90d horizons with risk level distribution), Cause Analysis (shortage cause breakdown by category — Manufacturing disruption, Raw material shortage, Regulatory action, Demand spike, Quality issue, Distribution issue), Alternative Tracker (select a shortage product to find therapeutic alternatives with pricing and risk comparison)' };

  if (pathname.startsWith('/briefings'))
    return { page_name: 'Executive Briefings', page_path: pathname, page_description: 'AI-generated executive briefing system with 4 views: Generator (select audience — C-Suite/Procurement/Compliance/Board, scope — Full Portfolio/Therapeutic Class/Manufacturer, time period, section selection, template picker), Preview (generated briefing with expandable sections, confidence badges, data points, section regeneration, PDF download), History (searchable table of past briefings with audience/scope filters, bulk export, duplicate, re-run), Templates (custom briefing templates)' };

  if (pathname === '/agent-activity')
    return { page_name: 'Agent Activity Dashboard', page_path: pathname, page_description: 'Overview of all scoring agents (COO Scoring, Quality Rating, Pricing Intelligence, COORS, Political Nexus, NDC Lookup) and intelligence agents (~25 agents). Shows per-agent: last run time, status, products processed, error count, health badge, circuit breaker state. Also shows: batch progress, Case Officer status, rescore-all and LLM analysis queue controls, API budget summary, COO Confidence Score history with tier distribution chart, and per-agent data completeness percentages.' };

  if (pathname === '/agent-detail/data_orchestrator')
    return { page_name: 'Case Officer Detail', page_path: pathname, page_description: 'Case Officer detail showing collection wave status, agent dispatch progress, evidence pipeline, and links to Evidence History page' };

  if (pathname.startsWith('/agent-detail/') && pathname.includes('/scored-products'))
    return { page_name: 'Scored Products', page_path: pathname, page_description: 'Products rescored by this agent showing: table with NDC, Product Name, Manufacturer, Original COO, Original Score, New COO, Latest Score, Scored At. Expandable rows show full factor breakdown grouped by data source (e.g., OpenFDA NDC, FDA Import Refusals, EPA TRI, etc.), with hit/miss status, weight, raw input data, and disabled factor indicators. Supports toggling between original and new COO factor views.' };

  if (pathname.startsWith('/agent-detail/'))
    return { page_name: 'Agent Detail', page_path: pathname, page_description: 'Scoring agent detail showing: agent status/health (last run, products processed, error rate, circuit breaker), full data source catalog with all factor definitions (e.g., COO Scoring has 16 sources with 59 factors), product search with individual rescore capability, toggle to enable/disable agent, and link to Scored Products page' };

  if (pathname.startsWith('/data-analyst/'))
    return { page_name: 'Data Analyst', page_path: pathname, page_description: 'Data analyst findings for a scoring agent with 3 tabs: Overview (summary stats — products analyzed, finding counts by type, score ring), Findings (paginated list of confidence overrides, reconciler conflicts, new sources, new factors, data discrepancies, missed factors, rescrape flags, logic/weight adjustments — filterable by type and scoring cycle), History (improvement history chart showing high-tier products and findings resolved over scoring cycles). Also has Products grid view for per-product finding breakdown.' };

  if (pathname === '/evidence-history')
    return { page_name: 'Evidence History', page_path: pathname, page_description: 'Agent evidence collection history showing: table of processed NDCs with columns (NDC, Agent Count, Found, Not Found, Errors, Last Collected). Click any row to expand and see per-agent evidence cards with status badge (Found/Not Found/Error/Skipped), collected timestamp, and full evidence data key-value display.' };

  if (pathname === '/self-attestations')
    return { page_name: 'Self-Attestations (Admin)', page_path: pathname, page_description: 'Admin view of all manufacturer self-attestation claims pending review' };

  if (pathname === '/my-attestations')
    return { page_name: 'My Attestations', page_path: pathname, page_description: 'Your submitted self-attestation claims and their review status' };

  if (pathname === '/apis')
    return { page_name: 'API Intelligence', page_path: pathname, page_description: 'API budget monitoring, usage analytics, and data source health dashboard' };

  return { page_name: 'The Trade Spy', page_path: pathname };
}

// ============================================================
// DYNAMIC QUICK ACTIONS PER PAGE
// ============================================================
function getQuickActions(pathname: string): QuickAction[] {
  if (pathname === '/') return [
    { text: 'Supply chain risk summary', query: 'Give me a quick intelligence briefing on the current state of our supply chain — key risks, shortage trends, and scoring health.' },
    { text: 'Biggest COO concerns', query: 'What are the biggest country of origin concerns right now? Which products have the lowest confidence scores?' },
    { text: 'China dependency overview', query: 'How dependent are we on China for active ingredients and finished goods? What are the riskiest therapeutic classes?' },
  ];

  if (pathname === '/agent-activity') return [
    { text: 'Agent health check', query: 'Give me a status report on all agents — who is healthy, who has errors, and what needs attention?' },
    { text: 'Last orchestrator run', query: 'How did the last case officer collection go? Any agent failures or data gaps?' },
    { text: 'Scoring trends', query: 'What are the scoring trends across recent runs? Are we improving or declining?' },
  ];

  if (pathname.startsWith('/agent-detail/') && !pathname.includes('/scored-products')) return [
    { text: 'Score change analysis', query: 'Compare the last two scoring runs. What changed and why? Which factors drove the biggest score movements?' },
    { text: 'Weakest factors', query: 'Which scoring factors have the lowest hit rates? Where are the biggest evidence gaps?' },
    { text: 'Improvement recommendations', query: 'Based on current factor hit rates and scoring trends, what are your top 3 recommendations to improve scoring quality?' },
  ];

  if (pathname.includes('/scored-products')) return [
    { text: 'Score distribution', query: 'Break down the score distribution — how many products are HIGH, MODERATE, and LOW? What is the average score?' },
    { text: 'Outlier products', query: 'Which products are outliers — unusually high or low scores? Are any scores suspicious?' },
    { text: 'Factor effectiveness', query: 'Which data sources and factors are contributing the most to scoring? Which are mostly empty?' },
  ];

  if (pathname === '/products') return [
    { text: 'High risk products', query: 'Which products have the highest risk profiles? Consider shortage status, low COO confidence, and China dependency.' },
    { text: 'US-origin products', query: 'Which products are confirmed US-origin with the highest COO confidence? What evidence supports their country of origin?' },
    { text: 'Data quality gaps', query: 'Which products have the most missing data or lowest evidence coverage from our intelligence agents?' },
  ];

  if (pathname.startsWith('/products/')) return [
    { text: 'Full product briefing', query: 'Give me a complete intelligence briefing on this product — scoring breakdown, evidence quality, risk factors, and recommendations.' },
    { text: 'Evidence analysis', query: 'What evidence do our intelligence agents have for this product? Where are the gaps and contradictions?' },
    { text: 'Scoring explanation', query: 'Explain this product\'s COO score step by step — which factors hit, which missed, and what would improve the score.' },
  ];

  if (pathname === '/china-dependency') return [
    { text: 'Critical dependencies', query: 'Which therapeutic classes have the most critical China dependency? Where are there no domestic alternatives?' },
    { text: 'Risk mitigation options', query: 'For our most China-dependent products, what alternative sourcing options exist? Are there domestic manufacturers?' },
    { text: 'Regulatory exposure', query: 'What regulatory and legislative risks apply to our China-dependent products? Any pending legislation that could impact supply?' },
  ];

  if (pathname.startsWith('/geopolitical')) return [
    { text: 'Country risk hotspots', query: 'Which countries have the highest risk levels right now? Look at active recalls, shortages, and product concentration to identify the most critical sourcing risks.' },
    { text: 'Geographic concentration risk', query: 'Where are we most concentrated geographically? Which countries have the most products but the lowest confidence in country of origin data?' },
  ];

  if (pathname.startsWith('/shortage-warning')) return [
    { text: 'Active shortages', query: 'What products are currently in shortage? What are the root causes and expected resolution timelines?' },
    { text: 'Shortage prediction', query: 'Based on current signals, which products are at risk of shortage? What early warning signs do you see?' },
  ];

  if (pathname === '/manufacturers' || pathname.startsWith('/manufacturers/')) return [
    { text: 'Manufacturer risk profile', query: 'Summarize the risk profile for manufacturers visible on this page — COO confidence, recall history, and regulatory issues.' },
    { text: 'Geographic concentration', query: 'How concentrated is our manufacturer base geographically? Where are the single points of failure?' },
  ];

  if (pathname.startsWith('/briefings')) return [
    { text: 'Weekly intelligence summary', query: 'Generate a weekly intelligence summary — key scoring changes, new findings, shortage updates, and agent collection highlights.' },
    { text: 'Improvement roadmap', query: 'Based on all available data, what should our top 5 priorities be for improving supply chain intelligence quality?' },
  ];

  if (pathname.startsWith('/data-analyst/')) return [
    { text: 'Open findings summary', query: 'Summarize all open findings — how many confidence overrides, reconciler conflicts, and contradictions are unresolved?' },
    { text: 'Priority findings', query: 'Which findings are highest priority? What should be investigated first?' },
  ];

  return [
    { text: 'What can you help with?', query: 'What kind of analysis and intelligence can you help me with on this page?' },
    { text: 'Data quality overview', query: 'Give me an overview of data quality across the system — coverage, freshness, and gaps.' },
  ];
}

// ============================================================
// MARKDOWN RENDERER
// ============================================================
function renderDirectorMessage(content: string): React.ReactNode {
  const parts = content.split(/(```[\s\S]*?```)/g);

  return parts.map((part, i) => {
    if (part.startsWith('```')) {
      const code = part.replace(/```\w*\n?/, '').replace(/```$/, '');
      return (
        <pre key={i} style={{
          background: '#1E293B', color: '#E2E8F0', padding: 12,
          borderRadius: 8, fontSize: 12, overflowX: 'auto',
          margin: '8px 0', lineHeight: 1.5,
        }}>
          {code}
        </pre>
      );
    }

    // Escape HTML first to prevent XSS, then apply markdown formatting
    const escaped = part
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return (
      <span key={i} dangerouslySetInnerHTML={{
        __html: escaped
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>')
          .replace(/`(.*?)`/g, '<code style="background:#F1F5F9;padding:2px 4px;border-radius:3px;font-size:12px">$1</code>')
          .replace(/\n/g, '<br/>')
      }} />
    );
  });
}

// ============================================================
// EXPERT AGENT COMPONENT — "THE DIRECTOR"
// ============================================================
export default function ExpertAgent({ user, currentPath }: ExpertAgentProps) {
  const { T } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<DirectorMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [convId, setConvId] = useState<string>(generateConvId());
  const [showHistory, setShowHistory] = useState(false);
  const [allConversations, setAllConversations] = useState<SavedConversation[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pageContext = getPageContext(currentPath);
  const quickActions = getQuickActions(currentPath);

  // Voice agent hook — adds real-time voice capability to The Director
  const handleVoiceTranscript = useCallback((role: 'user' | 'assistant', text: string) => {
    // Add voice transcripts to the chat message history
    setMessages(prev => {
      const updated = [...prev, { role, content: role === 'assistant' ? text : `🎙 ${text}` }];
      return updated;
    });
  }, []);
  const {
    voiceStatus,
    isVoiceActive,
    startVoice,
    stopVoice,
    transcripts: voiceTranscripts,
    errorMessage: voiceError,
  } = useVoiceAgent(pageContext, handleVoiceTranscript);

  // On page navigation: look for an existing conversation for this page, or start fresh
  useEffect(() => {
    const saved = loadConversations();
    const existing = saved.find(c => c.page_path === currentPath);
    if (existing && existing.messages.length > 0) {
      setMessages(existing.messages);
      setSessionId(existing.session_id);
      setConvId(existing.id);
    } else {
      setMessages([]);
      setSessionId(null);
      setConvId(generateConvId());
    }
    setShowHistory(false);
  }, [currentPath]);

  // Persist messages to localStorage whenever they change
  const persistMessages = useCallback((msgs: DirectorMessage[], sid: string | null, cid: string) => {
    if (msgs.length > 0) {
      upsertConversation(cid, currentPath, pageContext.page_name, msgs, sid);
    }
  }, [currentPath, pageContext.page_name]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current && !showHistory) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen, showHistory]);

  // Refresh conversation list when opening history
  useEffect(() => {
    if (showHistory) {
      setAllConversations(loadConversations());
    }
  }, [showHistory]);

  const sendMessage = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || loading) return;

    const userMessage: DirectorMessage = { role: 'user', content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/expert-agent/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          messages: newMessages,
          page_context: pageContext,
          session_id: sessionId,
        }),
      });

      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          const errBody = await response.json();
          if (errBody.detail) detail = errBody.detail;
        } catch { /* ignore parse errors */ }
        throw new Error(detail);
      }

      const data = await response.json();
      const reply = data.reply || "I couldn't process that request. Please try again.";
      const updatedMessages = [...newMessages, { role: 'assistant' as const, content: reply }];
      setMessages(updatedMessages);
      const newSessionId = data.session_id || sessionId;
      if (data.session_id && !sessionId) {
        setSessionId(newSessionId);
      }
      persistMessages(updatedMessages, newSessionId, convId);
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : 'Unknown error';
      const errorMessages = [...newMessages, {
        role: 'assistant' as const,
        content: `The Director encountered an error: ${errMsg}. Please try again.`,
      }];
      setMessages(errorMessages);
      persistMessages(errorMessages, sessionId, convId);
    } finally {
      setLoading(false);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setConvId(generateConvId());
    setShowHistory(false);
  };

  const loadConversation = (conv: SavedConversation) => {
    setMessages(conv.messages);
    setSessionId(conv.session_id);
    setConvId(conv.id);
    setShowHistory(false);
  };

  const removeConversation = (cid: string) => {
    deleteConversation(cid);
    setAllConversations(prev => prev.filter(c => c.id !== cid));
    // If deleting the current conversation, start fresh
    if (cid === convId) {
      startNewChat();
    }
  };

  return (
    <>
      {/* Floating Button — BOTTOM LEFT */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: 24,
          left: 24,
          width: 56,
          height: 56,
          borderRadius: 16,
          background: isOpen
            ? '#6B7280'
            : `linear-gradient(135deg, ${T.navy}, ${T.navyLight})`,
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
          zIndex: 997,
          transition: 'all 0.2s',
        }}
        title={isOpen ? 'Close Director' : 'Open The Director — Intelligence & Analysis'}
      >
        {isOpen ? <IconX /> : <IconDirector />}
      </button>

      {/* Chat Panel — BOTTOM LEFT */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: 90,
          left: 24,
          width: 440,
          maxWidth: 'calc(100vw - 48px)',
          height: 620,
          maxHeight: 'calc(100vh - 120px)',
          background: T.white,
          borderRadius: 16,
          boxShadow: T.shadowLg,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 996,
          animation: 'directorSlideIn 0.3s ease-out',
          fontFamily: "'DM Sans', -apple-system, sans-serif",
        }}>

          {/* Header */}
          <div style={{
            padding: 16,
            borderBottom: `1px solid ${T.border}`,
            background: T.red,
            borderRadius: '16px 16px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 8,
                background: T.navy,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <IconDirector />
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.white }}>
                  The Director
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                  Intelligence &amp; Analysis
                </div>
              </div>
            </div>
            {/* Header actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                padding: '3px 8px', borderRadius: 6, fontSize: 10,
                fontWeight: 600, color: 'rgba(255,255,255,0.8)',
                background: 'rgba(255,255,255,0.1)',
                maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {pageContext.page_name}
              </span>
              <button
                onClick={() => setShowHistory(!showHistory)}
                title="Conversation history"
                style={{
                  padding: '4px 8px', borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: showHistory ? 'rgba(255,255,255,0.15)' : 'transparent',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 11, cursor: 'pointer', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <IconHistory />
              </button>
              <button
                onClick={startNewChat}
                title="New conversation"
                style={{
                  padding: '4px 8px', borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'transparent', color: 'rgba(255,255,255,0.7)',
                  fontSize: 11, cursor: 'pointer', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <IconPlus />
              </button>
            </div>
          </div>

          {/* History Panel */}
          {showHistory && (
            <div style={{
              flex: 1, overflowY: 'auto', padding: 0,
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{
                padding: '12px 16px', borderBottom: `1px solid ${T.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <button
                  onClick={() => setShowHistory(false)}
                  style={{
                    padding: '4px 10px', borderRadius: 6,
                    border: `1px solid ${T.border}`, background: T.white,
                    fontSize: 12, cursor: 'pointer', fontWeight: 600,
                    color: T.textDark, display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <IconBack /> Back to chat
                </button>
                <span style={{ fontSize: 12, color: T.textMid, fontWeight: 600 }}>
                  {allConversations.length} conversation{allConversations.length !== 1 ? 's' : ''}
                </span>
              </div>
              {allConversations.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: T.textMid, fontSize: 13 }}>
                  No previous conversations yet.
                </div>
              ) : (
                allConversations.map(conv => {
                  const firstUserMsg = conv.messages.find(m => m.role === 'user');
                  const preview = firstUserMsg
                    ? firstUserMsg.content.slice(0, 80) + (firstUserMsg.content.length > 80 ? '...' : '')
                    : 'Empty conversation';
                  const date = new Date(conv.updated_at);
                  const timeStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                    + ' ' + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                  const isCurrent = conv.id === convId;
                  return (
                    <div
                      key={conv.id}
                      style={{
                        padding: '12px 16px', borderBottom: `1px solid ${T.border}`,
                        cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10,
                        background: isCurrent ? `${T.navyLight}08` : 'transparent',
                        transition: 'background 0.15s',
                      }}
                      onClick={() => loadConversation(conv)}
                      onMouseEnter={e => { if (!isCurrent) (e.currentTarget as HTMLDivElement).style.background = `${T.navyLight}05`; }}
                      onMouseLeave={e => { if (!isCurrent) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 11, fontWeight: 600, color: T.navyLight, marginBottom: 2,
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                          {conv.page_name}
                          {isCurrent && (
                            <span style={{
                              fontSize: 9, padding: '1px 5px', borderRadius: 4,
                              background: T.navyLight, color: T.white,
                            }}>current</span>
                          )}
                        </div>
                        <div style={{
                          fontSize: 12, color: T.textDark, lineHeight: 1.4,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {preview}
                        </div>
                        <div style={{ fontSize: 10, color: T.textLight, marginTop: 2 }}>
                          {timeStr} &middot; {conv.messages.length} message{conv.messages.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); removeConversation(conv.id); }}
                        title="Delete conversation"
                        style={{
                          padding: 4, borderRadius: 4, border: 'none',
                          background: 'transparent', color: T.textLight,
                          cursor: 'pointer', flexShrink: 0, marginTop: 2,
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#EF4444'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = T.textLight; }}
                      >
                        <IconTrash />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Messages Area */}
          {!showHistory && (
          <div ref={scrollRef} style={{
            flex: 1, overflowY: 'auto', padding: 16,
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            {/* Empty state with quick actions */}
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: `linear-gradient(135deg, ${T.navyLight}20, ${T.navy}10)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <IconDirectorNavy />
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.navy, marginBottom: 4 }}>
                  The Director
                </div>
                <div style={{ fontSize: 12, color: T.textMid, marginBottom: 4 }}>
                  Viewing: {pageContext.page_name}
                </div>
                <div style={{ fontSize: 13, color: T.textMid, marginBottom: 20, lineHeight: 1.5 }}>
                  Ask about scoring, data quality, risk patterns, or recommendations.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {quickActions.map((action, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(action.query)}
                      style={{
                        padding: '10px 14px', borderRadius: T.radiusSm,
                        border: `1px solid ${T.border}`, background: T.white,
                        fontSize: 13, fontWeight: 500, color: T.textDark,
                        cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.15s',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}
                      onMouseEnter={e => {
                        (e.target as HTMLButtonElement).style.borderColor = T.navyLight;
                        (e.target as HTMLButtonElement).style.background = `${T.navyLight}08`;
                      }}
                      onMouseLeave={e => {
                        (e.target as HTMLButtonElement).style.borderColor = T.border;
                        (e.target as HTMLButtonElement).style.background = T.white;
                      }}
                    >
                      <span style={{ color: T.navyLight, fontSize: 14, flexShrink: 0 }}>&#9670;</span>
                      {action.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '85%',
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user'
                    ? '12px 12px 4px 12px'
                    : '12px 12px 12px 4px',
                  background: msg.role === 'user' ? T.navy : T.offWhite,
                  color: msg.role === 'user' ? T.white : T.textDark,
                  fontSize: 13,
                  lineHeight: 1.6,
                }}>
                  {msg.role === 'assistant'
                    ? renderDirectorMessage(msg.content)
                    : msg.content}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '10px 14px', borderRadius: '12px 12px 12px 4px',
                  background: T.offWhite, display: 'flex', gap: 4,
                }}>
                  {[0, 1, 2].map(j => (
                    <div key={j} style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: T.navyLight,
                      animation: `dotPulse 1.4s ease-in-out ${j * 0.2}s infinite`,
                    }} />
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Voice Status Banner */}
          {isVoiceActive && !showHistory && (
            <div style={{
              padding: '8px 16px',
              background: voiceStatus === 'user_speaking'
                ? `${T.gold}15`
                : voiceStatus === 'agent_speaking'
                  ? `${T.navyLight}12`
                  : `${T.navy}08`,
              borderTop: `1px solid ${T.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'background 0.3s',
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: voiceStatus === 'error' ? '#EF4444'
                  : voiceStatus === 'user_speaking' ? T.gold
                  : voiceStatus === 'agent_speaking' ? T.navyLight
                  : '#10B981',
                animation: voiceStatus === 'listening' || voiceStatus === 'user_speaking' || voiceStatus === 'agent_speaking'
                  ? 'voicePulse 1.5s ease-in-out infinite'
                  : 'none',
              }} />
              <span style={{
                fontSize: 12, fontWeight: 600,
                color: voiceStatus === 'error' ? '#EF4444' : T.textMid,
              }}>
                {voiceStatusLabel(voiceStatus)}
              </span>
              {voiceError && (
                <span style={{ fontSize: 11, color: '#EF4444', marginLeft: 'auto' }}>
                  {voiceError}
                </span>
              )}
            </div>
          )}

          {/* Input Area */}
          {!showHistory && (
          <div style={{ padding: 16, borderTop: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyPress={e => { if (e.key === 'Enter') sendMessage(); }}
                placeholder={isVoiceActive ? 'Voice mode active — speak or type...' : 'Ask The Director...'}
                disabled={loading}
                style={{
                  flex: 1, padding: '10px 14px',
                  border: `2px solid ${isVoiceActive ? T.gold : T.border}`, borderRadius: T.radiusSm,
                  fontSize: 14, fontFamily: "'DM Sans', -apple-system, sans-serif",
                  outline: 'none', transition: 'border-color 0.2s',
                  color: T.textDark, background: T.white,
                }}
                onFocus={e => { e.target.style.borderColor = isVoiceActive ? T.gold : T.navyLight; }}
                onBlur={e => { e.target.style.borderColor = isVoiceActive ? T.gold : T.border; }}
              />
              {/* Voice toggle button */}
              {user.is_admin && (
                <button
                  onClick={() => {
                    if (isVoiceActive) {
                      stopVoice();
                    } else {
                      startVoice();
                    }
                  }}
                  disabled={loading || voiceStatus === 'connecting'}
                  title={isVoiceActive ? 'Stop voice mode' : 'Start voice mode'}
                  style={{
                    padding: '10px 12px', borderRadius: T.radiusSm,
                    border: isVoiceActive ? `2px solid ${T.gold}` : `2px solid ${T.border}`,
                    background: isVoiceActive
                      ? `${T.gold}18`
                      : 'transparent',
                    cursor: loading || voiceStatus === 'connecting' ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {isVoiceActive
                    ? <IconMicOff color="#EF4444" size={18} />
                    : <IconMic color={T.navyLight} size={18} />
                  }
                  {isVoiceActive && (
                    <div style={{
                      position: 'absolute', inset: 0,
                      borderRadius: T.radiusSm,
                      border: `2px solid ${T.gold}`,
                      animation: 'voiceRingPulse 2s ease-in-out infinite',
                      pointerEvents: 'none',
                    }} />
                  )}
                </button>
              )}
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                style={{
                  padding: '10px 16px', borderRadius: T.radiusSm,
                  border: 'none',
                  background: input.trim() && !loading ? T.navyLight : '#E5E7EB',
                  color: T.white,
                  cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s',
                }}
              >
                <IconSend />
              </button>
            </div>

          </div>
          )}
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes directorSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.3; }
          40% { opacity: 1; }
        }
        @keyframes voicePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes voiceRingPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0; transform: scale(1.15); }
        }
      `}</style>
    </>
  );
}
